import type { ClientFrame, ServerFrame } from "../shared/protocol.js";

export type ConnStatus = "connecting" | "open" | "reconnecting" | "fatal";

const PING_MS = 10_000;
/** Mobile sockets can die silently; treat this long without any server frame as dead. */
const DEAD_MS = 25_000;

/**
 * WebSocket that re-sends `hello` and backs off on every reconnect. There is no replay:
 * after a reconnect the client simply gets whatever is live from that moment.
 */
export class Conn {
  private ws?: WebSocket;
  private attempt = 0;
  private retryTimer?: number;
  private pingTimer?: number;
  private lastMessageAt = 0;
  private closed = false;
  status: ConnStatus = "connecting";

  constructor(
    private readonly opts: {
      hello: () => ClientFrame;
      onFrame: (f: ServerFrame) => void;
      onBinary?: (data: ArrayBuffer) => void;
      onStatus: (s: ConnStatus, detail?: string) => void;
    },
  ) {
    // Come back fast when the phone regains network or the page is shown again.
    addEventListener("online", () => this.kick());
    document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && this.kick());
  }

  connect() {
    this.closed = false;
    clearTimeout(this.retryTimer);
    const url = `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws`;
    const ws = new WebSocket(url);
    ws.binaryType = "arraybuffer";
    this.ws = ws;
    this.lastMessageAt = Date.now();

    ws.onopen = () => {
      ws.send(JSON.stringify(this.opts.hello()));
      clearInterval(this.pingTimer);
      this.pingTimer = window.setInterval(() => {
        if (Date.now() - this.lastMessageAt > DEAD_MS) return ws.close();
        this.send({ type: "ping" });
      }, PING_MS);
    };
    ws.onmessage = (e) => {
      this.lastMessageAt = Date.now();
      if (typeof e.data !== "string") return this.opts.onBinary?.(e.data as ArrayBuffer);
      const f = JSON.parse(e.data) as ServerFrame;
      if (f.type === "ready") {
        this.attempt = 0;
        this.setStatus("open");
      }
      if (f.type === "error" && f.fatal) {
        this.closed = true;
        this.setStatus("fatal", f.message);
      }
      this.opts.onFrame(f);
    };
    ws.onclose = () => {
      clearInterval(this.pingTimer);
      if (this.ws !== ws || this.closed) return;
      this.ws = undefined;
      this.scheduleRetry();
    };
  }

  private scheduleRetry() {
    this.setStatus("reconnecting");
    const base = Math.min(500 * 2 ** this.attempt, 10_000);
    this.attempt++;
    this.retryTimer = window.setTimeout(() => this.connect(), base * (0.75 + Math.random() * 0.5));
  }

  /** Reconnect now if we are waiting on a backoff timer. */
  private kick() {
    if (this.closed || this.ws) return;
    this.attempt = 0;
    this.connect();
  }

  send(f: ClientFrame) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(f));
  }

  sendBinary(data: Uint8Array<ArrayBuffer> | ArrayBuffer): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN || this.status !== "open") return false;
    // Real-time audio: if the uplink is congested, drop rather than queue delay.
    if (this.ws.bufferedAmount > 64 * 1024) return false;
    this.ws.send(data);
    return true;
  }

  close() {
    this.closed = true;
    clearTimeout(this.retryTimer);
    clearInterval(this.pingTimer);
    this.ws?.close();
    this.ws = undefined;
  }

  private setStatus(s: ConnStatus, detail?: string) {
    this.status = s;
    this.opts.onStatus(s, detail);
  }
}

const KEY_STORAGE = "transpansion.key";
let cachedKey: string | undefined;

/**
 * Access key for the control pages. Taken from ?key= once, remembered in this
 * browser, and removed from the address bar so it isn't shared by accident.
 */
export function accessKey(): string | undefined {
  if (cachedKey) return cachedKey;
  const params = new URLSearchParams(location.search);
  const fromUrl = params.get("key");
  if (fromUrl) {
    cachedKey = fromUrl;
    try {
      localStorage.setItem(KEY_STORAGE, fromUrl);
    } catch {
      /* private mode: the in-memory copy still works for this visit */
    }
    params.delete("key");
    const rest = params.toString();
    history.replaceState(null, "", location.pathname + (rest ? `?${rest}` : "") + location.hash);
    return cachedKey;
  }
  try {
    cachedKey = localStorage.getItem(KEY_STORAGE) ?? undefined;
  } catch {
    /* ignore */
  }
  return cachedKey;
}

let asking = false;

/** The server rejected the key: forget it and ask once (several panels may fail together). */
export function askForKey() {
  if (asking) return;
  asking = true;
  cachedKey = undefined;
  try {
    localStorage.removeItem(KEY_STORAGE);
  } catch {
    /* ignore */
  }
  const k = prompt("This screen needs the access key:");
  if (k) location.search = `?key=${encodeURIComponent(k.trim())}`;
}

/** Screen wake lock that re-acquires itself when the page becomes visible again. */
export class WakeLock {
  private sentinel?: { release(): Promise<void> };
  private wanted = false;

  constructor() {
    document.addEventListener("visibilitychange", () => {
      if (this.wanted && document.visibilityState === "visible") void this.acquire();
    });
  }

  async enable() {
    this.wanted = true;
    await this.acquire();
  }

  async disable() {
    this.wanted = false;
    await this.sentinel?.release().catch(() => {});
    this.sentinel = undefined;
  }

  private async acquire() {
    try {
      const wl = (navigator as any).wakeLock;
      if (wl) this.sentinel = await wl.request("screen");
    } catch {
      /* not supported or refused (e.g. low battery); nothing to do */
    }
  }
}

export type EventInfo = { name: string; code: string; joinUrl: string };

export async function fetchEvent(): Promise<EventInfo> {
  const r = await fetch("/api/event");
  return r.json();
}

export const $ = <T extends HTMLElement = HTMLElement>(sel: string) => document.querySelector(sel) as T;
